import { AlertCircle, Check, ExternalLink, FileImage, LoaderCircle, Pause, Play, RotateCcw, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Tooltip } from "@/components/motion/tooltip";
import { EASE_OUT, SPRING_LAYOUT, SPRING_PRESS } from "@/lib/ease";
import { PresenceGate } from "@/lib/presence-gate";
import { cn } from "@/lib/utils";
import { AttachmentUploadItem, ITEM_TRANSITION, UPLOAD_PROGRESS_MS, WAVEFORM_BARS, formatBytes, formatDuration, AttachmentIcon, imageSource, RowActionState } from "./model";
export function RowAction({
  label,
  onClick,
  state,
  retryable = false,
  reduce = false,
}: {
  label: string;
  onClick: () => void;
  state: RowActionState;
  retryable?: boolean;
  reduce?: boolean;
}) {
  if (state === "uploading") {
    return <span aria-hidden="true" className="size-9 shrink-0" />;
  }

  if (state === "complete") {
    return (
      <Tooltip content="Upload complete" side="top" delay={100}>
        <motion.span
          role="status"
          aria-label={`Upload complete for ${label}`}
          initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.75 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={ITEM_TRANSITION}
          className="grid size-9 shrink-0 place-items-center rounded-xl text-emerald-600 dark:text-emerald-400"
        >
          <Check className="size-4" />
        </motion.span>
      </Tooltip>
    );
  }

  if (state === "removing") {
    return (
      <Tooltip content="Removing attachment" side="top" delay={100}>
        <span
          role="status"
          aria-label={`Removing ${label}`}
          className="grid size-9 shrink-0 place-items-center rounded-xl text-mut"
        >
          <motion.span
            animate={reduce ? undefined : { rotate: 360 }}
            transition={{
              duration: 0.7,
              ease: "linear",
              repeat: Infinity,
            }}
            className="grid place-items-center"
          >
            <LoaderCircle className="size-4" />
          </motion.span>
        </span>
      </Tooltip>
    );
  }

  if (state === "failed") {
    if (!retryable) {
      return (
        <Tooltip content="Upload failed" side="top" delay={100}>
          <span
            role="status"
            aria-label={`Upload failed for ${label}`}
            className="grid size-9 shrink-0 place-items-center rounded-xl text-brand-red"
          >
            <AlertCircle className="size-4" />
          </span>
        </Tooltip>
      );
    }

    return (
      <Tooltip content="Retry upload" side="top" delay={100}>
        <motion.button
          type="button"
          aria-label={`Retry ${label}`}
          onClick={onClick}
          whileTap={reduce ? undefined : { scale: 0.92 }}
          transition={SPRING_PRESS}
          className="grid size-9 shrink-0 place-items-center rounded-xl text-brand-red outline-none transition-colors hover:bg-brand-red/10 focus-visible:ring-2 focus-visible:ring-navy"
        >
          <RotateCcw className="size-4" />
        </motion.button>
      </Tooltip>
    );
  }

  return (
    <Tooltip content="Remove attachment" side="top" delay={100}>
      <motion.button
        type="button"
        aria-label={`Remove ${label}`}
        onClick={onClick}
        whileTap={reduce ? undefined : { scale: 0.92 }}
        transition={SPRING_PRESS}
        className="grid size-9 shrink-0 place-items-center rounded-xl text-mut outline-none transition-colors hover:bg-navy-50 hover:text-navy focus-visible:ring-2 focus-visible:ring-navy"
      >
        <X className="size-4" />
      </motion.button>
    </Tooltip>
  );
}

export function ImageThumbnail({
  item,
  layoutId,
  onPreview,
  reduce,
}: {
  item: AttachmentUploadItem;
  layoutId?: string;
  onPreview: (item: AttachmentUploadItem) => void;
  reduce: boolean;
}) {
  const src = imageSource(item);

  if (!src) {
    return (
      <span
        aria-hidden="true"
        className="grid size-8 shrink-0 place-items-center rounded-lg bg-navy-50 text-mut"
      >
        <FileImage className="size-4" />
      </span>
    );
  }

  return (
    <Tooltip
      side="top"
      delay={160}
      className="rounded-xl p-1 shadow-xl"
      content={
        <span className="block w-32">
          {/* biome-ignore lint/performance/noImgElement: Blob and remote previews keep this registry component framework-agnostic. */}
          <img
            src={src}
            alt=""
            className="h-20 w-full rounded-lg object-cover"
          />
          <span className="block px-1 pb-0.5 pt-1 text-center text-[10px] font-medium text-mut">
            Click to preview
          </span>
        </span>
      }
    >
      <motion.button
        type="button"
        aria-label={`Preview ${item.name}`}
        onClick={(event) => {
          event.currentTarget.blur();
          onPreview(item);
        }}
        whileTap={reduce ? undefined : { scale: 0.94 }}
        transition={SPRING_PRESS}
        className="group/image relative size-9 shrink-0 overflow-hidden rounded-[10px] bg-navy-50 outline-none ring-1 ring-line/70 focus-visible:ring-2 focus-visible:ring-navy"
      >
        {/* biome-ignore lint/performance/noImgElement: Motion layout requires the image element and portable blob URLs. */}
        <motion.img
          layoutId={layoutId}
          src={src}
          alt=""
          className="size-full object-cover"
          transition={{ layout: SPRING_LAYOUT }}
        />
      </motion.button>
    </Tooltip>
  );
}

export function ImagePreviewDialog({
  item,
  layoutId,
  onClose,
  reduce,
}: {
  item: AttachmentUploadItem | null;
  layoutId?: string;
  onClose: () => void;
  reduce: boolean;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!item) return;

    const previousFocus =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "Tab") {
        event.preventDefault();
        closeRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [item, onClose]);

  if (typeof document === "undefined") return null;

  const src = item ? imageSource(item) : undefined;
  const content =
    item && src ? (
      // The wrapper carries no box: both children are `fixed` and resolve
      // against the viewport themselves. The scrim spans the viewport edges but
      // paints a colour, and the layer that centres the image is inset off every
      // edge. `PresenceGate` releases interaction in the same commit that starts
      // the exit.
      <PresenceGate>
        {({ isPresent, gate }) => (
          <div
            {...(!isPresent ? { inert: "" } : {})}
            className="pointer-events-none fixed left-0 top-0 z-[10000] size-0"
          >
            <motion.button
              type="button"
              aria-label="Close image preview"
              tabIndex={-1}
              className="pointer-events-auto fixed inset-0 size-full cursor-default bg-black/45 backdrop-blur-xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={reduce ? undefined : { opacity: 0 }}
              transition={{ duration: reduce ? 0.1 : 0.2, ease: EASE_OUT }}
              {...gate}
              onClick={onClose}
            />

            <div className="fixed inset-4 flex items-center justify-center sm:inset-8">
              <motion.div
                role="dialog"
                aria-modal="true"
                aria-label={`Preview of ${item.name}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={reduce ? undefined : { opacity: 0 }}
                transition={ITEM_TRANSITION}
                {...gate}
                className="pointer-events-auto relative"
              >
                {/* biome-ignore lint/performance/noImgElement: Motion layout requires the image element and portable blob URLs. */}
                <motion.img
                  layoutId={reduce ? undefined : layoutId}
                  src={src}
                  alt={item.name}
                  className="max-h-[90vh] max-w-[90vw] rounded-2xl object-contain shadow-2xl"
                  transition={{ layout: SPRING_LAYOUT }}
                />
                <motion.button
                  ref={closeRef}
                  type="button"
                  aria-label="Close image preview"
                  onClick={onClose}
                  initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={
                    reduce ? undefined : { opacity: 0, scale: 0.8 }
                  }
                  whileTap={reduce ? undefined : { scale: 0.92 }}
                  transition={SPRING_PRESS}
                  className="absolute -right-3 -top-3 grid size-9 place-items-center rounded-full bg-white text-navy shadow-xl outline-none ring-1 ring-line/70 transition-colors hover:bg-navy-50 focus-visible:ring-2 focus-visible:ring-navy"
                >
                  <X className="size-4" />
                </motion.button>
              </motion.div>
            </div>
          </div>
        )}
      </PresenceGate>
    ) : null;

  return createPortal(
    reduce ? content : <AnimatePresence>{content}</AnimatePresence>,
    document.body,
  );
}

export function AttachmentRow({
  item,
  playing,
  uploading,
  uploadComplete,
  failed,
  removing,
  arrivalIndex,
  imageLayoutId,
  onAudioToggle,
  onImagePreview,
  onRemove,
  onRetry,
  reduce,
  className,
  actions,
}: {
  item: AttachmentUploadItem;
  playing: boolean;
  uploading: boolean;
  uploadComplete: boolean;
  failed: boolean;
  removing: boolean;
  arrivalIndex: number;
  imageLayoutId?: string;
  onAudioToggle?: (item: AttachmentUploadItem) => void;
  onImagePreview: (item: AttachmentUploadItem) => void;
  onRemove: (item: AttachmentUploadItem) => void;
  onRetry?: (item: AttachmentUploadItem) => void;
  reduce: boolean;
  className?: string;
  actions?: ReactNode;
}) {
  const size = formatBytes(item.size);
  const progress =
    item.duration && item.duration > 0
      ? Math.min(1, Math.max(0, (item.currentTime ?? 0) / item.duration))
      : 0;
  const actionState: RowActionState = removing
    ? "removing"
    : uploading
      ? "uploading"
      : uploadComplete
        ? "complete"
        : failed
          ? "failed"
          : "idle";
  const arrivalDelay = Math.min(Math.max(arrivalIndex, 0), 5) * 0.055;
  const rowTransition =
    !reduce && arrivalIndex >= 0
      ? {
          ...SPRING_LAYOUT,
          delay: arrivalDelay,
          opacity: {
            duration: 0.16,
            ease: EASE_OUT,
            delay: arrivalDelay,
          },
        }
      : ITEM_TRANSITION;
  const showUploadProgress = uploading || uploadComplete;
  const uploadProgress = (
    <motion.span
      role="progressbar"
      aria-label={`Uploading ${item.name}`}
      className="pointer-events-none absolute inset-0 -z-10 origin-left bg-emerald-400/25 dark:bg-emerald-500/20"
      initial={{ opacity: 1, scaleX: 0 }}
      animate={{ opacity: 1, scaleX: 1 }}
      exit={reduce ? undefined : { opacity: 0 }}
      transition={{
        duration: reduce ? 0.1 : UPLOAD_PROGRESS_MS / 1000,
        ease: EASE_OUT,
      }}
    />
  );

  return (
    <motion.li
      layout={!reduce}
      initial={
        reduce
          ? { opacity: 0 }
          : arrivalIndex >= 0
            ? { opacity: 0, y: -16, scale: 0.985 }
            : { opacity: 0, y: 6 }
      }
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={reduce ? undefined : { opacity: 0, y: -4 }}
      transition={rowTransition}
      className={cn(
        "flex min-h-14 items-center gap-1 rounded-2xl bg-navy-50/70 p-1",
        className,
      )}
    >
      <div className="relative isolate flex min-w-0 flex-1 items-center gap-3 self-stretch overflow-hidden rounded-xl bg-white px-2 py-1">
        {failed ? (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 -z-10 bg-brand-red/10"
          />
        ) : null}

        {item.kind === "image" ? (
          <ImageThumbnail
            item={item}
            layoutId={imageLayoutId}
            onPreview={onImagePreview}
            reduce={reduce}
          />
        ) : (
          <span
            aria-hidden="true"
            className="grid size-7 shrink-0 place-items-center text-mut"
          >
            <AttachmentIcon kind={item.kind} />
          </span>
        )}

        {item.kind === "audio" ? (
          <>
            <span className="w-9 shrink-0 text-xs tabular-nums text-mut">
              {formatDuration(item.currentTime)}
            </span>
            <span
              aria-hidden="true"
              className="flex h-11 min-w-0 flex-1 items-center gap-[3px] overflow-hidden"
            >
              {WAVEFORM_BARS.map((bar, index) => (
                <motion.span
                  key={bar.id}
                  className={cn(
                    "w-[3px] shrink-0 rounded-full",
                    index / WAVEFORM_BARS.length <= progress
                      ? "bg-navy"
                      : "bg-mut/35",
                  )}
                  style={{ height: bar.height }}
                  animate={
                    reduce || !playing
                      ? undefined
                      : { scaleY: [0.72, 1, 0.78] }
                  }
                  transition={{
                    duration: 0.55,
                    ease: EASE_OUT,
                    repeat: Infinity,
                    delay: index * 0.018,
                  }}
                />
              ))}
            </span>
            <span className="w-9 shrink-0 text-right text-xs tabular-nums text-mut">
              {formatDuration(item.duration)}
            </span>
            <motion.button
              type="button"
              aria-label={`${playing ? "Pause" : "Play"} ${item.name}`}
              onClick={() => onAudioToggle?.(item)}
              whileTap={{ scale: 0.94 }}
              transition={SPRING_PRESS}
              className="grid size-9 shrink-0 place-items-center rounded-full bg-navy text-white outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2 focus-visible:ring-offset-white"
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={playing ? "pause" : "play"}
                  initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.8 }}
                  transition={ITEM_TRANSITION}
                >
                  {playing ? (
                    <Pause className="size-4 fill-current" />
                  ) : (
                    <Play className="size-4 translate-x-px fill-current" />
                  )}
                </motion.span>
              </AnimatePresence>
            </motion.button>
          </>
        ) : (
          <>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-navy">
                {item.name}
              </span>
              {failed ? (
                <span className="block truncate text-[11px] text-brand-red">
                  {item.error ?? "Upload failed"}
                </span>
              ) : null}
            </span>
            <span className="shrink-0 text-xs text-mut">
              {item.kind === "link" ? "Web" : size}
            </span>
            {item.kind === "link" && item.href ? (
              <a
                href={item.href}
                target="_blank"
                rel="noreferrer noopener"
                aria-label={`Open ${item.name}`}
                className="grid size-8 shrink-0 place-items-center rounded-lg text-mut outline-none transition-colors hover:bg-navy-50 hover:text-navy focus-visible:ring-2 focus-visible:ring-navy"
              >
                <ExternalLink className="size-4" />
              </a>
            ) : null}
          </>
        )}

        {reduce ? (
          showUploadProgress ? (
            uploadProgress
          ) : null
        ) : (
          <AnimatePresence>
            {showUploadProgress ? uploadProgress : null}
          </AnimatePresence>
        )}
      </div>

      {actions}
      <RowAction
        label={item.name}
        onClick={() => {
          if (actionState === "failed") {
            onRetry?.(item);
            return;
          }
          onRemove(item);
        }}
        state={actionState}
        retryable={onRetry !== undefined}
        reduce={reduce}
      />
    </motion.li>
  );
}
